import { getCfg } from "./config";
import { refreshAccessToken, cancelAuthentication } from "./auth";
import { createAuthenticatedFetch } from "./request";
import { withRequestDeadline } from "./network";

// Token storage + the /api/* callers, ported from app.html. Tokens live in
// sessionStorage exactly as before, so they clear on tab close.
export const tokenGet = (): string | null => sessionStorage.getItem("access_token");
export const tokenClear = (): void => {
  cancelAuthentication();
  try { sessionStorage.removeItem("access_token"); }
  finally { sessionStorage.removeItem("refresh_token"); }
};

export const AUTH_EXPIRED_EVENT = "life-os:auth-expired";

const expireSession = (): void => {
  tokenClear();
  if (typeof window !== "undefined") window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
};

const authenticatedFetch = createAuthenticatedFetch({
  getAccessToken: tokenGet,
  refresh: refreshAccessToken,
  onExpired: expireSession,
});

export async function api<T = unknown>(path: string): Promise<T> {
  // Vault bodies load in the background and can take longer over a slow link.
  return withRequestDeadline(async (signal) => {
    const r = await authenticatedFetch(getCfg().apiBase + path, { signal });
    if (!r.ok) throw new Error("api " + path + " → " + r.status);
    return r.json() as Promise<T>;
  }, path.startsWith("/api/vault") ? 60_000 : 15_000);
}

// Persist a form action. Forms update the store optimistically and fire this; a
// failure is logged (a refresh reconciles from the server). Live path only.
export async function postApi(path: string, body: unknown): Promise<Response> {
  return withRequestDeadline(async (signal) => {
    const r = await authenticatedFetch(getCfg().apiBase + path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
    if (!r.ok) {
      let message = "Couldn't save the change (" + r.status + "). Retry the same submission.";
      try {
        const result: unknown = await r.clone().json();
        if (result && typeof result === "object" && "error" in result && typeof result.error === "string" && result.error.length <= 300) message = result.error;
      } catch { /* Keep the bounded fallback for non-JSON gateway failures. */ }
      throw new Error(message);
    }
    // Retain the original Response for callers that read receipt URLs. Reading
    // its clone completes the body within the same deadline, rather than letting
    // a later r.json() hang after we have already reported the write as saved.
    await r.clone().arrayBuffer();
    return r;
  }, 25_000);
}
